/**
 * Energy units with exact factors in joules.
 *
 * Every factor is a definition, not a measurement, and is exact:
 *
 * - **Calories.** The thermochemical calorie is 4.184 J and the International Table (IT)
 *   calorie 4.1868 J, both as listed in NIST Special Publication 811 (2008 edition),
 *   Appendix B. The kilocalorie — the "Calorie" on a food label — is 1000 of either.
 * - **British thermal units.** BTU (IT) = 1055.05585262 J and BTU (thermochemical) =
 *   1054.350 J, again from SP 811. The two differ by about 0.07 %.
 * - **Electronvolt.** 1 eV = 1.602176634 × 10⁻¹⁹ J exactly, because the 2019 redefinition of
 *   the SI fixed the elementary charge at 1.602176634 × 10⁻¹⁹ C.
 * - **Watt-hours.** 1 Wh = 3600 J, 1 kWh = 3.6 MJ.
 * - **Foot-pound force.** The pound-force is the pound (0.45359237 kg) times standard
 *   gravity (9.80665 m/s²), and the foot is 0.3048 m, so 1 ft·lbf = 1.3558179483314004 J.
 * - **Erg.** 1 erg = 10⁻⁷ J (the CGS unit).
 * - **Litre-atmosphere.** 1 L·atm = 10⁻³ m³ × 101 325 Pa = 101.325 J.
 * - **Tonne of TNT.** By convention 4.184 × 10⁹ J, so that a kiloton is 4.184 × 10¹² J.
 */

import {
  decimal,
  multiply,
  powerOfTen,
  type Ratio,
} from "@/lib/tool-kit/calendars/exact";

export interface EnergyUnitDef {
  id: string;
  symbol: string;
  joules: Ratio;
}

const ELECTRONVOLT = multiply(decimal("1.602176634"), powerOfTen(-19));

const FOOT_POUND_FORCE = multiply(
  multiply(decimal("0.45359237"), decimal("9.80665")),
  decimal("0.3048")
);

export const ENERGY_UNITS: readonly EnergyUnitDef[] = [
  { id: "j", symbol: "J", joules: decimal("1") },
  { id: "kj", symbol: "kJ", joules: decimal("1000") },
  { id: "mj", symbol: "MJ", joules: decimal("1000000") },
  { id: "wh", symbol: "Wh", joules: decimal("3600") },
  { id: "kwh", symbol: "kWh", joules: decimal("3600000") },
  { id: "calth", symbol: "cal (th)", joules: decimal("4.184") },
  { id: "kcalth", symbol: "kcal (th)", joules: decimal("4184") },
  { id: "calit", symbol: "cal (IT)", joules: decimal("4.1868") },
  { id: "kcalit", symbol: "kcal (IT)", joules: decimal("4186.8") },
  { id: "btuit", symbol: "BTU (IT)", joules: decimal("1055.05585262") },
  { id: "btuth", symbol: "BTU (th)", joules: decimal("1054.350") },
  { id: "ev", symbol: "eV", joules: ELECTRONVOLT },
  { id: "kev", symbol: "keV", joules: multiply(ELECTRONVOLT, powerOfTen(3)) },
  { id: "mev", symbol: "MeV", joules: multiply(ELECTRONVOLT, powerOfTen(6)) },
  { id: "erg", symbol: "erg", joules: decimal("0.0000001") },
  { id: "ftlbf", symbol: "ft·lbf", joules: FOOT_POUND_FORCE },
  { id: "latm", symbol: "L·atm", joules: decimal("101.325") },
  { id: "tnt", symbol: "t TNT", joules: decimal("4184000000") },
];
